import React, { useCallback } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import { mdiAccountMultiple, mdiLoading, mdiMagnify, mdiPen, mdiPlus, mdiTrashCan, mdiRefresh, mdiWifi, mdiFileImport, mdiCheckCircle, mdiAlertCircle, mdiCheck, mdiClose, mdiLaptop, mdiChartLine, mdiChip, mdiMemory, mdiHarddisk, mdiMicrosoftWindows, mdiLinux, mdiApple, mdiUbuntu, mdiDebian, mdiDesktopClassic, mdiShieldAlertOutline, mdiLinkOff, mdiRestart, mdiPrinter, mdiDesktopTower, mdiDesktopTowerMonitor, mdiServer, mdiDevices, mdiTelevisionClassic, mdiAccountMultiplePlus, mdiAccountMultiplePlusOutline } from '@mdi/js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastSuccess, toastWarning } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { useDebounce } from '@/lib/useDebounce';
import { DeviceGroupRefDTO, NetworkLanDeviceDTO, ArpDiscoveredDeviceDTO, NetworkLanDeviceTypeDTO } from '@webip/shared/src/dto/appliance';
import { TelemetryCurrentResourceDTO } from '@webip/shared/src/dto/telemetry';
import { OsPatchDeviceSummaryDTO, OsPatchMissingItemDTO, OsPatchSeverityDTO } from '@webip/shared/src/dto/os-patch';
import StatusDot from '@/components/layout/StatusDot';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import { FormLanDevice } from '@/dashboard/FormLanDevice';
import { useAppliance } from '@/lib/useAppliance';
import InstalarAgenteHostModal from '@/pages/dispositivos/InstalarAgenteHostModal';
import Descricao from '@/components/tipografia/Descricao';
import Papa from 'papaparse';
import {
    getTelemetryTone,
    getUsagePercent,
    formatPercent,
    formatUsage,
    formatUsagePercent,
    formatCollectedAtShort,
} from '@/lib/telemetryFormat';
import TelemetryOverviewModal from '@/components/TelemetryOverviewModal';
import { IconeTipoDispositivoLan } from '@/components/IconeTipoDispositivoLan';

// ─── Grupos de dispositivos ───────────────────────────────────────────────────

type GroupFormState = {
    id?: number;
    nome: string;
    descricao: string;
    applianceId: string | null;
    deviceIds: number[];
};

const emptyGroupForm = (applianceId: string | null): GroupFormState => ({
    nome: '',
    descricao: '',
    applianceId,
    deviceIds: [],
});

function GruposDispositivoManagerModal({ onClose }: { onClose: () => void }) {
    const qc = useQueryClient();
    const { applianceId } = useAppliance();

    const [editObject, setEditObject] = React.useState<GroupFormState | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [membrosModal, setMembrosModal] = React.useState<GroupFormState | null>(null);

    const groupsQuery = useQuery(
        ['grupo_dispositivo', applianceId],
        async () => {
            if (!applianceId) return [];
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                `/api/grupo_dispositivo?appliance_id=${applianceId}`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: !!applianceId }
    );

    const devicesQuery = useQuery(
        ['lan_dispositivo-all', applianceId],
        async () => {
            if (!applianceId) return [];
            const params = new URLSearchParams({
                fi: JSON.stringify({ appliance_id: +applianceId! }),
                t: '500',
            });
            const res = await axios.get<{ result: NetworkLanDeviceDTO[] }>(
                `/api/lan_dispositivo?${params}`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: !!applianceId }
    );

    const devices = devicesQuery.data ?? [];
    const groups = groupsQuery.data ?? [];

    const changeMutator = useMutation(
        async () => {
            if (!editObject || !editObject.applianceId) return;
            setFieldErrors({});
            const payload: Record<string, unknown> = {
                nome: editObject.nome,
                descricao: editObject.descricao,
                applianceId: +editObject.applianceId,
            };
            if (editObject.id) {
                return await axios.put(`/api/grupo_dispositivo/${editObject.id}`, payload, {
                    withCredentials: true,
                });
            } else {
                return await axios.post('/api/grupo_dispositivo', payload, { withCredentials: true });
            }
        },
        {
            onSuccess: () => {
                setEditObject(null);
                groupsQuery.refetch();
                qc.invalidateQueries(['grupo_dispositivo']);
                toastSuccess('Grupo salvo com sucesso');
            },
            onError: (err: unknown) => {
                const { message, fieldErrors: errs } = extractApiError(err, 'Erro ao salvar grupo');
                if (errs) setFieldErrors(errs);
                toastWarning(message);
            },
        }
    );

    const deleteMutator = useMutation(
        async (id: number) =>
            axios.delete(`/api/grupo_dispositivo/${id}`, { withCredentials: true }),
        {
            onSuccess: () => {
                groupsQuery.refetch();
                qc.invalidateQueries(['grupo_dispositivo']);
                toastSuccess('Grupo removido');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao remover grupo');
                toastWarning(message);
            },
        }
    );

    const membrosMutator = useMutation(
        async () => {
            if (!membrosModal) return;
            return await axios.put(
                `/api/grupo_dispositivo/${membrosModal.id}/membros`,
                { deviceIds: membrosModal.deviceIds },
                { withCredentials: true }
            );
        },
        {
            onSuccess: () => {
                setMembrosModal(null);
                groupsQuery.refetch();
                qc.invalidateQueries(['grupo_dispositivo']);
                toastSuccess('Membros atualizados');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao atualizar membros');
                toastWarning(message);
            },
        }
    );

    const handleSaveGroup = useCallback(() => changeMutator.mutate(), [changeMutator]);
    const handleSaveMembros = useCallback(() => membrosMutator.mutate(), [membrosMutator]);

    const handleManageMembros = (row: DeviceGroupRefDTO) => {
        setMembrosModal({
            id: row.id,
            nome: row.nome,
            descricao: row.descricao ?? '',
            applianceId,
            deviceIds: [...row.deviceIds],
        });
    };

    const toggleDevice = (deviceId: number) => {
        if (!membrosModal) return;
        const ids = membrosModal.deviceIds.includes(deviceId)
            ? membrosModal.deviceIds.filter((id) => id !== deviceId)
            : [...membrosModal.deviceIds, deviceId];
        setMembrosModal({ ...membrosModal, deviceIds: ids });
    };

    const groupHeaders: DataTableHeader[] = [
        { label: 'Nome', column: 'nome' },
        { label: 'Descrição', column: 'descricao' },
        {
            label: 'Dispositivos',
            width: '110px',
            render: (row: DeviceGroupRefDTO) => String(row.deviceIds.length),
        },
        {
            label: '',
            width: '120px',
            alignRight: true,
            render: (row: DeviceGroupRefDTO) => (
                <HBox gap="6px" style={{ justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="none-secondary"
                        icon={mdiAccountMultiple}
                        onClick={() => handleManageMembros(row)}
                        title="Gerenciar membros"
                    />
                    <Botao
                        size="sm"
                        variant="none-primary"
                        icon={mdiPen}
                        onClick={() => {
                            setEditObject({
                                id: row.id,
                                nome: row.nome,
                                descricao: row.descricao ?? '',
                                applianceId,
                                deviceIds: row.deviceIds,
                            });
                            setFieldErrors({});
                        }}
                    />
                    <BotaoPerigoso
                        size="sm"
                        variant="none-danger"
                        icon={mdiTrashCan}
                        onClick={() => deleteMutator.mutate(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <>
            <Modal show title="Grupos de Dispositivos" onClose={onClose} size="lg">
                <VBox style={{ gap: '12px', minWidth: '560px' }}>
                    <HBox style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            variant="primary"
                            icon={mdiPlus}
                            onClick={() => {
                                setEditObject(emptyGroupForm(applianceId));
                                setFieldErrors({});
                            }}
                            disabled={!applianceId}
                        >
                            Novo grupo
                        </Botao>
                    </HBox>
                    <DataTable
                        cabecalhos={groupHeaders}
                        linhas={groups}
                        isLoading={groupsQuery.isFetching}
                    />
                </VBox>
            </Modal>

            {/* Sub-modal de criação/edição de grupo */}
            {editObject && (
                <Modal
                    ignoreOverlayClick
                    title={editObject.id ? 'Editar grupo' : 'Novo grupo de dispositivos'}
                    show
                    onClose={() => setEditObject(null)}
                    onCancel={() => setEditObject(null)}
                    onConfirm={handleSaveGroup}
                >
                    <VBox gap="12px">
                        <CampoTexto
                            label="Nome"
                            value={editObject.nome}
                            onChange={(v) => setEditObject({ ...editObject, nome: v })}
                            error={fieldErrors?.nome}
                        />
                        <CampoTexto
                            label="Descrição"
                            value={editObject.descricao}
                            onChange={(v) => setEditObject({ ...editObject, descricao: v })}
                            error={fieldErrors?.descricao}
                        />
                    </VBox>
                </Modal>
            )}

            {/* Sub-modal de membros */}
            {membrosModal && (
                <Modal
                    ignoreOverlayClick
                    title={`Membros: ${membrosModal.nome}`}
                    show
                    onClose={() => setMembrosModal(null)}
                    onCancel={() => setMembrosModal(null)}
                    onConfirm={handleSaveMembros}
                >
                    <VBox gap="8px">
                        <Descricao>
                            Selecione os dispositivos que fazem parte deste grupo.
                        </Descricao>
                        {devicesQuery.isFetching && (
                            <Icon path={mdiLoading} size="14px" spin />
                        )}
                        {devices.length === 0 && !devicesQuery.isFetching && (
                            <span>Nenhum dispositivo cadastrado para este appliance.</span>
                        )}
                        {devices.map((d) => (
                            <label
                                key={d.id}
                                style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}
                            >
                                <input
                                    type="checkbox"
                                    checked={membrosModal.deviceIds.includes(d.id!)}
                                    onChange={() => toggleDevice(d.id!)}
                                />
                                <span>
                                    {d.nome} <small style={{ color: 'var(--tc-color-gray-500)' }}>({d.ip})</small>
                                </span>
                            </label>
                        ))}
                    </VBox>
                </Modal>
            )}
        </>
    );
}

// ─── Importar CSV ────────────────────────────────────────────────────────────

type CsvRow = { ip: string; nome: string; mac: string };
type CsvImportError = { linha: number; ip: string; nome: string; mac: string; erro: string };
type CsvImportResult = { ok: boolean; imported: number; errors: CsvImportError[] };

function parseCsvPreview(text: string): { rows: CsvRow[]; error: string | null } {
    const result = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        transform: (v) => v.trim(),
    });

    if (result.errors.length > 0 && result.data.length === 0) {
        return { rows: [], error: `Erro ao ler CSV: ${result.errors[0].message}` };
    }

    const sample = result.data[0] ?? {};
    if (!('ip' in sample) || !('nome' in sample) || !('mac' in sample)) {
        return { rows: [], error: 'O CSV deve conter as colunas: ip, nome, mac.' };
    }

    const rows: CsvRow[] = result.data.map((r) => ({
        ip: r['ip'] ?? '',
        nome: r['nome'] ?? '',
        mac: r['mac'] ?? '',
    }));

    return { rows, error: null };
}

function ImportarCsvModal({
    applianceId,
    onClose,
    onImported,
}: {
    applianceId: string | null;
    onClose: () => void;
    onImported: () => void;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [preview, setPreview] = React.useState<CsvRow[]>([]);
    const [parseError, setParseError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<CsvImportResult | null>(null);

    const importMutation = useMutation(
        async () => {
            if (!file || !applianceId) return;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('appliance_id', applianceId);
            const res = await axios.post<CsvImportResult>(
                '/api/lan_dispositivo/import-csv',
                formData,
                { withCredentials: true },
            );
            return res.data;
        },
        {
            onSuccess: (data) => {
                if (!data) return;
                setResult(data);
                if (data.imported > 0) onImported();
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao importar CSV');
                toastWarning(message);
            },
        }
    );

    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const canImport = !!file && !parseError && preview.length > 0 && !result;

    const processFile = (f: File) => {
        setFile(f);
        setResult(null);
        setParseError(null);
        const reader = new FileReader();
        reader.onload = (ev) => {
            const { rows, error } = parseCsvPreview(ev.target?.result as string);
            setParseError(error);
            setPreview(rows);
        };
        reader.readAsText(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) processFile(f);
    };

    const thStyle: React.CSSProperties = {
        padding: '7px 12px', textAlign: 'left', fontWeight: 600, fontSize: '12px',
        color: 'var(--tc-color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em',
        borderBottom: '1px solid var(--tc-color-gray-200)',
        position: 'sticky', top: 0, zIndex: 1,
        backgroundColor: 'var(--tc-color-gray-100)',
    };
    const tdStyle: React.CSSProperties = {
        padding: '7px 12px', fontSize: '13px', color: 'var(--tc-color-gray-800)',
        borderBottom: '1px solid var(--tc-color-gray-100)',
    };

    return (
        <Modal
            show
            ignoreOverlayClick
            title="Importar dispositivos via CSV"
            size="lg"
            onClose={onClose}
            onCancel={result ? undefined : onClose}
            onConfirm={result ? onClose : () => importMutation.mutate()}
            confirmProps={{
                label: result ? 'Fechar' : importMutation.isLoading ? 'Importando…' : 'Importar',
                disabled: result ? false : (!canImport || importMutation.isLoading),
                variant: result ? 'outline-secondary' : 'primary',
            }}
            cancelProps={{ visible: !result }}
        >
            <VBox gap="16px" style={{ minWidth: '480px' }}>

                {/* ── Fase 1: seleção de arquivo ── */}
                {!result && (
                    <>
                        {/* Zona de drop */}
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            style={{
                                border: `2px dashed ${dragging ? 'var(--tc-color-primary-400)' : file ? 'var(--tc-color-primary-300)' : 'var(--tc-color-gray-300)'}`,
                                borderRadius: '10px',
                                padding: '28px 20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: dragging ? 'var(--tc-color-primary-50)' : file ? 'var(--tc-color-primary-50)' : 'var(--tc-color-gray-50)',
                                transition: 'border-color 0.15s, background 0.15s',
                                userSelect: 'none',
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                style={{ display: 'none' }}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }}
                            />
                            <Icon
                                path={mdiFileImport}
                                size="32px"
                                color={file ? 'var(--tc-color-primary-400)' : 'var(--tc-color-gray-400)'}
                                style={{ display: 'block', margin: '0 auto 10px' }}
                            />
                            {file ? (
                                <VBox gap="4px" style={{ alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--tc-color-primary-700)' }}>
                                        {file.name}
                                    </span>
                                    {!parseError && preview.length > 0 && (
                                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                                            {preview.length} linha{preview.length !== 1 ? 's' : ''} encontrada{preview.length !== 1 ? 's' : ''} · clique para trocar
                                        </span>
                                    )}
                                </VBox>
                            ) : (
                                <VBox gap="4px" style={{ alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--tc-color-gray-600)' }}>
                                        Arraste o arquivo aqui ou clique para selecionar
                                    </span>
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-400)' }}>
                                        Colunas obrigatórias: <strong>ip</strong>, <strong>nome</strong>, <strong>mac</strong> — vírgula ou ponto-e-vírgula
                                    </span>
                                </VBox>
                            )}
                        </div>

                        {/* Erro de parse */}
                        {parseError && (
                            <HBox gap="8px" style={{ alignItems: 'center', color: 'var(--tc-text-color-danger-emphasis)', background: 'var(--tc-background-color-danger-subtle)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--tc-border-color-danger-subtle)' }}>
                                <Icon path={mdiAlertCircle} size="18px" style={{ flexShrink: 0 }} />
                                <span style={{ fontSize: '13px' }}>{parseError}</span>
                            </HBox>
                        )}

                        {/* Tabela de preview */}
                        {!parseError && preview.length > 0 && (
                            <div style={{ border: '1px solid var(--tc-color-gray-200)', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>IP</th>
                                            <th style={thStyle}>Nome</th>
                                            <th style={thStyle}>MAC</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map((row, idx) => (
                                            <tr key={idx} style={{ background: idx % 2 === 1 ? 'var(--tc-color-gray-50)' : 'white' }}>
                                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{row.ip || <em style={{ color: 'var(--tc-color-gray-300)' }}>—</em>}</td>
                                                <td style={tdStyle}>{row.nome || <em style={{ color: 'var(--tc-color-gray-300)' }}>—</em>}</td>
                                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{row.mac || <em style={{ color: 'var(--tc-color-gray-300)' }}>—</em>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ── Fase 2: resultado ── */}
                {result && (
                    <VBox gap="14px">
                        {/* Banner de sucesso */}
                        <HBox gap="12px" style={{
                            alignItems: 'center',
                            background: result.imported > 0 ? 'var(--tc-background-color-success-subtle)' : 'var(--tc-color-gray-100)',
                            border: `1px solid ${result.imported > 0 ? 'var(--tc-border-color-success-subtle)' : 'var(--tc-color-gray-200)'}`,
                            padding: '14px 16px', borderRadius: '10px',
                        }}>
                            <Icon
                                path={mdiCheckCircle}
                                size="28px"
                                color={result.imported > 0 ? 'var(--status-ok)' : 'var(--tc-color-gray-400)'}
                                style={{ flexShrink: 0 }}
                            />
                            <VBox gap="2px">
                                <span style={{ fontWeight: 700, fontSize: '15px', color: result.imported > 0 ? 'var(--tc-text-color-success-emphasis)' : 'var(--tc-color-gray-600)' }}>
                                    {result.imported} dispositivo{result.imported !== 1 ? 's' : ''} importado{result.imported !== 1 ? 's' : ''} com sucesso
                                </span>
                                {result.errors.length > 0 && (
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                                        {result.errors.length} linha{result.errors.length !== 1 ? 's' : ''} ignorada{result.errors.length !== 1 ? 's' : ''} por erro
                                    </span>
                                )}
                            </VBox>
                        </HBox>

                        {/* Tabela de erros */}
                        {result.errors.length > 0 && (
                            <VBox gap="8px">
                                <HBox gap="6px" style={{ alignItems: 'center' }}>
                                    <Icon path={mdiAlertCircle} size="16px" color="var(--tc-text-color-danger-emphasis)" />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--tc-text-color-danger-emphasis)' }}>
                                        Linhas com erro
                                    </span>
                                </HBox>
                                <div style={{ border: '1px solid var(--tc-border-color-danger-subtle)', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...thStyle, width: '52px', textAlign: 'center' }}>#</th>
                                                <th style={thStyle}>Nome</th>
                                                <th style={thStyle}>IP</th>
                                                <th style={thStyle}>Motivo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.errors.map((e) => (
                                                <tr key={e.linha} style={{ background: 'var(--tc-background-color-danger-subtle)', borderBottom: '1px solid var(--tc-border-color-danger-subtle)' }}>
                                                    <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--tc-color-gray-400)', fontVariantNumeric: 'tabular-nums' }}>{e.linha}</td>
                                                    <td style={tdStyle}>{e.nome || <em style={{ color: 'var(--tc-color-gray-400)' }}>—</em>}</td>
                                                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{e.ip || <em style={{ color: 'var(--tc-color-gray-400)' }}>—</em>}</td>
                                                    <td style={{ ...tdStyle, color: 'var(--tc-text-color-danger-emphasis)' }}>{e.erro}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </VBox>
                        )}
                    </VBox>
                )}
            </VBox>
        </Modal>
    );
}

type ResponseList = {
    ok?: boolean;
    result: NetworkLanDeviceDTO[];
    total: number;
    cursor: string | number | null;
};

// Mesmo raciocínio de staleness usado no backend para telemetria de appliance
// (RepoTelemetryResourceSnapshot.STALE_THRESHOLD_MS): o host-agent reporta a
// cada ~60s, então 3 ciclos sem report é o limiar razoável para "offline".
const AGENT_STALE_THRESHOLD_MS = 3 * 60 * 1000;

function isAgentOnline(agentLastSeenAt?: string | null): boolean {
    if (!agentLastSeenAt) return false;
    const ageMs = Date.now() - new Date(agentLastSeenAt).getTime();
    return Number.isFinite(ageMs) && ageMs <= AGENT_STALE_THRESHOLD_MS;
}

// ─── Última atividade na rede (ultima_atividade_em, carimbada pelo IPFIX) ────

// O backend só regrava o carimbo a cada ~5 min, então "agora" cobre esse passo.
const ATIVIDADE_RECENTE_MS = 10 * 60 * 1000;
const ATIVIDADE_ANTIGA_MS = 4 * 60 * 60 * 1000;

function isAtividadeRecente(iso?: string | null): boolean {
    if (!iso) return false;
    const ageMs = Date.now() - new Date(iso).getTime();
    return Number.isFinite(ageMs) && ageMs <= ATIVIDADE_RECENTE_MS;
}

function isAtividadeAntiga(iso?: string | null): boolean {
    if (!iso) return false;
    const ageMs = Date.now() - new Date(iso).getTime();
    return Number.isFinite(ageMs) && ageMs > ATIVIDADE_ANTIGA_MS;
}

function formatUltimaAtividade(iso?: string | null): string {
    if (!iso) return 'nunca visto';
    const ageMs = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ageMs)) return '—';
    if (ageMs < ATIVIDADE_RECENTE_MS) return 'visto agora';
    const min = Math.floor(ageMs / 60_000);
    if (min < 60) return `visto há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `visto há ${h} h`;
    const d = Math.floor(h / 24);
    return d === 1 ? 'visto há 1 dia' : `visto há ${d} dias`;
}

/** Grupos do dispositivo como texto discreto, ao lado do "visto há". */
function renderGruposCell(row: NetworkLanDeviceDTO, grupos: DeviceGroupRefDTO[]) {
    const nomes = grupos.filter((g) => g.deviceIds.includes(row.id!)).map((g) => g.nome);
    if (nomes.length === 0) return null;
    const MAX_VISIVEIS = 2;
    const visiveis = nomes.slice(0, MAX_VISIVEIS).join(', ');
    const resto = nomes.length - MAX_VISIVEIS;
    return (
        <span
            title={nomes.length > MAX_VISIVEIS ? nomes.join(', ') : undefined}
            style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}
        >
            {' · '}{visiveis}{resto > 0 ? ` +${resto}` : ''}
        </span>
    );
}

function renderUltimaAtividadeCell(row: NetworkLanDeviceDTO) {
    const iso = row.ultima_atividade_em;
    const recente = isAtividadeRecente(iso);
    return (
        <span
            title={iso ? new Date(iso).toLocaleString('pt-BR') : 'Sem tráfego registrado para este IP'}
            style={{
                fontSize: '12px',
                color: 'var(--tc-color-gray-500)',
                fontWeight: recente ? 600 : undefined,
            }}
        >
            {formatUltimaAtividade(iso)}
        </span>
    );
}

// ─── Telemetria (mesmo layout usado em appliances/page.tsx) ──────────────────

function renderDeviceTelemetryCell(row: NetworkLanDeviceDTO, telemetryMap: Map<number, TelemetryCurrentResourceDTO>) {
    if (!row.machine_uid) {
        return <span style={{ color: 'var(--tc-color-gray-300)' }}>sem agente</span>;
    }

    const telemetry = row.id != null ? telemetryMap.get(row.id) : undefined;
    const collectedAtText = formatCollectedAtShort(telemetry?.collected_at);
    const cpuTone = getTelemetryTone(telemetry?.current?.cpu_percent);
    const memoryPercent = getUsagePercent(telemetry?.current?.memory_used_bytes, telemetry?.current?.memory_total_bytes);
    const memoryTone = getTelemetryTone(memoryPercent);
    const diskPercent = getUsagePercent(telemetry?.current?.disk_used_bytes, telemetry?.current?.disk_total_bytes);
    const diskTone = getTelemetryTone(diskPercent);

    return (
        <VBox style={{ gap: '4px', minWidth: 0, width: '100%' }}>
            <HBox style={{ gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    color: cpuTone.color,
                    background: cpuTone.background,
                    fontWeight: 600,
                    borderRadius: '999px',
                    padding: '2px 8px',
                }}>
                    <Icon path={mdiChip} size="14px" />
                    {formatPercent(telemetry?.current?.cpu_percent)}
                </span>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    color: memoryTone.color,
                    background: memoryTone.background,
                    fontWeight: 600,
                    borderRadius: '999px',
                    padding: '2px 8px',
                }}>
                    <Icon path={mdiMemory} size="14px" />
                    {formatUsagePercent(telemetry?.current?.memory_used_bytes, telemetry?.current?.memory_total_bytes)}
                </span>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    color: diskTone.color,
                    background: diskTone.background,
                    fontWeight: 600,
                    borderRadius: '999px',
                    padding: '2px 8px',
                }}>
                    <Icon path={mdiHarddisk} size="14px" />
                    {formatUsagePercent(telemetry?.current?.disk_used_bytes, telemetry?.current?.disk_total_bytes)}
                </span>
            </HBox>
            <HBox stretch style={{ fontSize: '11px', color: 'var(--color-muted)' }}>
                <span>{formatOsLabel(row.os_platform, row.os_version, row.os_arch)}</span> <span style={{ textWrap: 'nowrap' }}>{collectedAtText}</span>
                {!isAgentOnline(row.agent_last_seen_at) && <Badge variant="warning" style={{ marginLeft: 'auto' }}>
                    <StatusDot status='offline' showLabel={false} /> Agente offline
                </Badge>}
            </HBox>
        </VBox>
    );
}

const osToIcon = {
    'windows': mdiMicrosoftWindows,
    'linux': mdiLinux,
    'apple': mdiApple,
    'ubuntu': mdiUbuntu,
    'debian': mdiDebian,
    'unknown': mdiDesktopClassic,
}

function formatOsLabel(platform?: string | null, version?: string | null, arch?: string | null) {
    let _platform = platform?.toLowerCase() ?? 'unknown'
    if (_platform.indexOf('windows') >= 0) _platform = 'windows';
    if (_platform.indexOf('linux') >= 0) _platform = 'linux';
    if (_platform.indexOf('ubuntu') >= 0) _platform = 'ubuntu';
    if (_platform.indexOf('debian') >= 0) _platform = 'debian';
    if (_platform.indexOf('mac') >= 0 || _platform.indexOf('osx') >= 0 || _platform.indexOf('darwin') >= 0) _platform = 'apple';
    const icon = osToIcon[_platform as keyof typeof osToIcon];

    const details = `${platform ?? 'Desconhecido'}\n${version ?? ''}\n${arch ?? ''}`.trim();

    return <HBox>
        <Icon path={icon} size="24px" title={details} color="var(--tc-color-gray-500)" />
    </HBox>
}

// ─── Patches de SO ────────────────────────────────────────────────────────────

const SEVERITY_META: Record<OsPatchSeverityDTO, { label: string; variant: BadgeVariants }> = {
    critical: { label: 'crítico', variant: 'danger' },
    high: { label: 'alto', variant: 'danger' },
    medium: { label: 'médio', variant: 'warning' },
    low: { label: 'baixo', variant: 'secondary' },
    unknown: { label: 'não classificado', variant: 'secondary' },
};

const SEVERITY_ORDER: OsPatchSeverityDTO[] = ['critical', 'high', 'medium', 'low', 'unknown'];

// Ícone de aviso "vai pedir reinício" — mostrado ANTES de aplicar os
// patches, pra planejamento, não uma constatação pós-instalação.
function RebootHintIcon({ count }: { count: number }) {
    if (!count) return null;
    return (
        <Icon
            path={mdiRestart}
            size="16px"
            color="var(--tc-color-warning-500, #b45309)"
            title={`${count} patch${count !== 1 ? 'es' : ''} pendente${count !== 1 ? 's' : ''} pode${count !== 1 ? 'm' : ''} exigir reinício após aplicado${count !== 1 ? 's' : ''}`}
        />
    );
}

function renderDevicePatchesCell(row: NetworkLanDeviceDTO, osPatchMap: Map<number, OsPatchDeviceSummaryDTO>) {
    if (!row.machine_uid) {
        return <span style={{ color: 'var(--tc-color-gray-300)' }}>sem agente</span>;
    }

    const summary = row.id != null ? osPatchMap.get(row.id) : undefined;
    if (!summary || !summary.last_scan_at) {
        return <span style={{ color: 'var(--tc-color-gray-300)' }}>sem varredura</span>;
    }

    if (summary.missing_count === 0) {
        return <Badge variant="success">Em dia</Badge>;
    }

    // macOS não tem fonte pública de severidade — nunca inventamos uma;
    // mostramos a contagem sem badge de severidade.
    const allUnclassified = summary.package_manager === 'softwareupdate';
    if (allUnclassified) {
        return (
            <HBox style={{ gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                    {summary.missing_count} pendente{summary.missing_count !== 1 ? 's' : ''} · severidade não disponível
                </span>
                <RebootHintIcon count={summary.reboot_hint_count} />
            </HBox>
        );
    }

    const worst = SEVERITY_ORDER.find((sev) => summary.by_severity[sev] > 0) ?? 'unknown';
    const meta = SEVERITY_META[worst];
    return (
        <HBox style={{ gap: '6px', alignItems: 'center' }}>
            <Badge variant={meta.variant}>
                {summary.by_severity[worst]} {meta.label}{summary.by_severity[worst] !== 1 ? 's' : ''}
            </Badge>
            <RebootHintIcon count={summary.reboot_hint_count} />
        </HBox>
    );
}

export default function DispositivosPage() {
    const [search, setSearch] = React.useState('');
    /** id do grupo, 'nenhum' (fora de qualquer grupo) ou null (todos). */
    const [grupoFiltro, setGrupoFiltro] = React.useState<string | null>(null);
    const [editObject, setEditObject] = React.useState<Partial<NetworkLanDeviceDTO> | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [showGruposModal, setShowGruposModal] = React.useState(false);
    const [showImportCsvModal, setShowImportCsvModal] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState<'cadastrados' | 'pendentes' | 'descobertos'>('cadastrados');
    const [editGrupoIds, setEditGrupoIds] = React.useState<number[]>([]);
    const [telemetryDevice, setTelemetryDevice] = React.useState<NetworkLanDeviceDTO | null>(null);
    const [osPatchDevice, setOsPatchDevice] = React.useState<NetworkLanDeviceDTO | null>(null);
    const [agenteHostDevice, setAgenteHostDevice] = React.useState<NetworkLanDeviceDTO | null>(null);
    const debouncedSearch = useDebounce(search, 500);

    const { applianceId, applianceOptions, selectedAppliance, appliances } = useAppliance();

    /** agent_id do adapter da appliance do dispositivo — null quando ela não
     *  tem agente vinculado (a modal explica o que fazer nesse caso). */
    const agenteIdDispositivo = (row: NetworkLanDeviceDTO | null): string | null => {
        const alvo = row?.appliance_id ?? (applianceId ? +applianceId : null);
        if (alvo == null) return null;
        return appliances.find(a => a.id === alvo)?.agent_id ?? null;
    };

    const queryTelemetryCurrent = useQuery(
        ['telemetry-devices-current', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; items: TelemetryCurrentResourceDTO[] }>(
                `/api/telemetry/resources/current?resource_type=device&appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.items;
        },
        { enabled: !!applianceId, refetchInterval: 60_000 },
    );

    const telemetryMap = React.useMemo(() => {
        const map = new Map<number, TelemetryCurrentResourceDTO>();
        for (const item of queryTelemetryCurrent.data ?? []) {
            map.set(item.resource_id, item);
        }
        return map;
    }, [queryTelemetryCurrent.data]);

    const queryOsPatchesCurrent = useQuery(
        ['os-patches-devices-current', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; items: OsPatchDeviceSummaryDTO[] }>(
                `/api/os-patches/devices/current?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.items;
        },
        { enabled: !!applianceId, refetchInterval: 60_000 },
    );

    const osPatchMap = React.useMemo(() => {
        const map = new Map<number, OsPatchDeviceSummaryDTO>();
        for (const item of queryOsPatchesCurrent.data ?? []) {
            map.set(item.dispositivo_id, item);
        }
        return map;
    }, [queryOsPatchesCurrent.data]);

    const queryOsPatchDetail = useQuery(
        ['os-patches-device-detail', osPatchDevice?.id],
        async () => {
            const res = await axios.get<{ ok: boolean; result: { missing: OsPatchMissingItemDTO[] } }>(
                `/api/os-patches/devices/${osPatchDevice!.id}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        { enabled: osPatchDevice?.id != null },
    );

    const gruposQuery = useQuery(
        ['grupo_dispositivo', applianceId],
        async () => {
            if (!applianceId) return [];
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                `/api/grupo_dispositivo?appliance_id=${applianceId}`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: !!applianceId }
    );

    // Grupos são por appliance: o filtro não sobrevive à troca de appliance.
    React.useEffect(() => setGrupoFiltro(null), [applianceId]);
    // Grupo filtrado removido (pelo "Gerenciar grupos") volta para "todos".
    React.useEffect(() => {
        if (!grupoFiltro || grupoFiltro === 'nenhum' || !gruposQuery.data) return;
        if (!gruposQuery.data.some((g) => String(g.id) === grupoFiltro)) setGrupoFiltro(null);
    }, [gruposQuery.data, grupoFiltro]);

    const discoveredQuery = useQuery(
        ['arp_discovered', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: ArpDiscoveredDeviceDTO[]; agentOffline?: boolean; error?: string }>(
                `/api/lan_dispositivo/discovered?appliance_id=${applianceId}`,
                { withCredentials: true }
            );
            return res.data;
        },
        { enabled: !!applianceId && activeTab === 'descobertos', retry: false }
    );

    const queryList = useInfiniteQuery({
        queryKey: ['lan_dispositivo', applianceId, debouncedSearch, grupoFiltro],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('txt', debouncedSearch);
            if (grupoFiltro) params.append('grupo', grupoFiltro);
            if (applianceId) params.append('fi', JSON.stringify({ appliance_id: +applianceId, aguardando_adocao: false }));
            if (pageParam) params.append('c', String(pageParam));
            return await axios.get<ResponseList>(`/api/lan_dispositivo?${params.toString()}`, {
                withCredentials: true,
            });
        },
        getNextPageParam: (lastPage) => lastPage.data.cursor ?? null,
        enabled: !!applianceId,
    });

    // Dispositivos auto-registrados por um host-agent, ainda não aceitos pelo
    // admin. Busca eagerly (não só quando a aba está aberta) para que o selo
    // de contagem apareça assim que a página carrega — diferente da aba
    // "Descobertos", aqui o admin normalmente não sabe de antemão que um
    // host-agent foi instalado em algum lugar.
    const pendentesQuery = useQuery(
        ['lan_dispositivo-pendentes', applianceId],
        async () => {
            if (!applianceId) return [];
            const params = new URLSearchParams({
                fi: JSON.stringify({ appliance_id: +applianceId, aguardando_adocao: true }),
                t: '200',
            });
            const res = await axios.get<{ result: NetworkLanDeviceDTO[] }>(
                `/api/lan_dispositivo?${params}`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: !!applianceId }
    );

    const aceitarMutator = useMutation(
        async (id: number) => axios.post(`/api/lan_dispositivo/${id}/agente/aceitar`, {}, { withCredentials: true }),
        {
            onSuccess: () => {
                pendentesQuery.refetch();
                queryList.refetch();
                toastSuccess('Dispositivo adotado');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao aceitar dispositivo');
                toastWarning(message);
            },
        }
    );

    const rejeitarMutator = useMutation(
        async (id: number) => axios.post(`/api/lan_dispositivo/${id}/agente/rejeitar`, {}, { withCredentials: true }),
        {
            onSuccess: () => {
                pendentesQuery.refetch();
                toastSuccess('Dispositivo rejeitado');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao rejeitar dispositivo');
                toastWarning(message);
            },
        }
    );

    const changeMutator = useMutation(
        async () => {
            if (!editObject) return;
            setFieldErrors({});
            let savedId: number | undefined;
            if (editObject.id) {
                await axios.put(`/api/lan_dispositivo/${editObject.id}`, editObject, { withCredentials: true });
                savedId = editObject.id;
            } else {
                const res = await axios.post<{ result?: { id?: number } }>('/api/lan_dispositivo', editObject, { withCredentials: true });
                savedId = res.data.result?.id;
            }
            if (savedId) {
                await axios.put(`/api/lan_dispositivo/${savedId}/grupos`, { grupoIds: editGrupoIds }, { withCredentials: true });
            }
        },
        {
            onSuccess: () => {
                setEditObject(null);
                queryList.refetch();
                gruposQuery.refetch();
                toastSuccess('Dispositivo salvo com sucesso');
            },
            onError: (err: unknown) => {
                const { message, fieldErrors: errs } = extractApiError(err, 'Erro ao salvar dispositivo');
                if (errs) setFieldErrors(errs);
                toastWarning(message);
            },
        }
    );

    const deleteMutator = useMutation(
        async (id: number) => axios.delete(`/api/lan_dispositivo/${id}`, { withCredentials: true }),
        {
            onSuccess: () => {
                queryList.refetch();
                toastSuccess('Dispositivo removido');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao remover dispositivo');
                toastWarning(message);
            },
        }
    );

    const descadastrarAgenteMutator = useMutation(
        async (id: number) => axios.post(`/api/lan_dispositivo/${id}/agente/descadastrar`, {}, { withCredentials: true }),
        {
            onSuccess: () => {
                queryList.refetch();
                queryTelemetryCurrent.refetch();
                queryOsPatchesCurrent.refetch();
                toastSuccess('Agente de host descadastrado');
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao descadastrar agente de host');
                toastWarning(message);
            },
        }
    );

    const handleSave = useCallback(() => changeMutator.mutate(), [changeMutator]);
    const handleDelete = (id: number) => deleteMutator.mutate(id);

    React.useEffect(() => {
        if (!editObject?.id || !gruposQuery.data) return;
        setEditGrupoIds(
            gruposQuery.data.filter(g => g.deviceIds.includes(editObject.id!)).map(g => g.id)
        );
    }, [gruposQuery.data, editObject?.id]);

    const handleNew = () => {
        setEditObject({ appliance_id: applianceId ? +applianceId : undefined } as Partial<NetworkLanDeviceDTO>);
        setEditGrupoIds([]);
        setFieldErrors({});
    };

    const handleEdit = (row: NetworkLanDeviceDTO) => {
        setEditObject(row);
        setEditGrupoIds([]);
        setFieldErrors({});
    };

    const handleCloseModal = () => {
        setEditObject(null);
        setEditGrupoIds([]);
        setFieldErrors({});
    };

    const queryFlatList = queryList.data?.pages?.flatMap((p) => p.data.result) ?? [];
    const total = queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    const headers: DataTableHeader[] = [
        {
            label: 'Nome', width: '250px', render(row) {
                const cor = isAtividadeRecente(row.ultima_atividade_em)
                    ? 'var(--status-online)'
                    : isAtividadeAntiga(row.ultima_atividade_em)
                        ? 'var(--tc-color-warning-600)'
                        : 'var(--tc-color-gray-400)'
                return <VBox gap={2}>
                    <HBox gap={4}>
                        <IconeTipoDispositivoLan tipo={row.tipo} cor={cor} />&nbsp;
                        <button
                            type="button"
                            onClick={() => handleEdit(row)}
                            title="Editar dispositivo"
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                color: 'var(--tc-color-primary-600)',
                                cursor: 'pointer',
                                textAlign: 'left',
                            }}
                        >
                            {row.nome}
                        </button>
                    </HBox>
                    <span>
                        {renderUltimaAtividadeCell(row)}
                        {renderGruposCell(row, gruposQuery.data ?? [])}
                    </span>
                </VBox>
            }
        },
        {
            label: 'IP', width: '150px', render(row) {
                return <VBox style={{ fontFamily: 'monospace', fontSize: '12px' }} gap={2}>
                    <span>{row.ip}</span>
                    <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>{row.mac}</span>
                </VBox>
            }
        },
        {
            label: 'Telemetria',
            width: '240px',
            render: (row: NetworkLanDeviceDTO) => renderDeviceTelemetryCell(row, telemetryMap),
        },
        {
            label: 'Patches',
            width: '160px',
            render: (row: NetworkLanDeviceDTO) => renderDevicePatchesCell(row, osPatchMap),
        },
        {
            label: 'Ações',
            width: '230px',
            stickRight: true,
            alignRight: true,
            render: (row: NetworkLanDeviceDTO) => (
                <HBox gap={2} style={{ justifyContent: 'flex-end' }}>
                    {row.machine_uid && (
                        <Botao
                            size="sm"
                            variant="none-secondary"
                            icon={mdiChartLine}
                            onClick={() => setTelemetryDevice(row)}
                            title="Abrir telemetria"
                        />
                    )}
                    {row.machine_uid && (
                        <Botao
                            size="sm"
                            variant="none-secondary"
                            icon={mdiShieldAlertOutline}
                            onClick={() => setOsPatchDevice(row)}
                            title="Abrir patches de segurança"
                        />
                    )}
                    {row.machine_uid && (
                        <BotaoPerigoso
                            size="sm"
                            variant="none-warning"
                            icon={mdiLinkOff}
                            title="Descadastrar agente de host"
                            confirmation={
                                <span>
                                    Remover o vínculo do agente de host deste dispositivo? O cadastro da
                                    máquina permanece, mas telemetria e patches deixam de ser coletados.
                                    Se o agente ainda estiver rodando nela, ela volta a se registrar no
                                    próximo report — desinstale-o na máquina primeiro.
                                </span>
                            }
                            onConfirm={() => descadastrarAgenteMutator.mutate(row.id!)}
                        />
                    )}
                    {!row.machine_uid && (
                        <Botao
                            size="sm"
                            variant="none-secondary"
                            icon={mdiLaptop}
                            onClick={() => setAgenteHostDevice(row)}
                            title="Instalar agente de host"
                        />
                    )}
                    <Botao size="sm" variant="none-primary" icon={mdiPen} onClick={() => handleEdit(row)} />
                    <BotaoPerigoso
                        size="sm"
                        variant="none-danger"
                        icon={mdiTrashCan}
                        onClick={() => handleDelete(row.id!)}
                    />
                </HBox>
            ),
        },
    ];

    const pendentesHeaders: DataTableHeader[] = [
        {
            label: 'Dispositivo',
            render: (row: NetworkLanDeviceDTO) => (
                <HBox gap="8px" style={{ alignItems: 'center' }}>
                    <Icon path={mdiLaptop} size="16px" color="var(--tc-color-gray-500)" />
                    <span style={{ fontWeight: 600 }}>{row.nome}</span>
                </HBox>
            ),
        },
        { label: 'IP', column: 'ip', width: '150px' },
        { label: 'MAC', column: 'mac', width: '180px' },
        {
            label: 'SO',
            width: '180px',
            render: (row: NetworkLanDeviceDTO) => (
                <span style={{ fontSize: '12px', color: row.os_platform ? 'var(--color-body)' : 'var(--tc-color-gray-300)' }}>
                    {formatOsLabel(row.os_platform, row.os_version, row.os_arch)}
                </span>
            ),
        },
        {
            label: 'Visto por último',
            width: '170px',
            render: (row: NetworkLanDeviceDTO) =>
                row.agent_last_seen_at ? new Date(row.agent_last_seen_at).toLocaleString('pt-BR') : '—',
        },
        {
            label: 'Ações',
            width: '190px',
            alignRight: true,
            stickRight: true,
            render: (row: NetworkLanDeviceDTO) => (
                <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="success"
                        icon={mdiCheck}
                        disabled={aceitarMutator.isLoading}
                        onClick={() => aceitarMutator.mutate(row.id!)}
                    >
                        Aceitar
                    </Botao>
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        icon={mdiClose}
                        disabled={rejeitarMutator.isLoading}
                        onClick={() => rejeitarMutator.mutate(row.id!)}
                    >
                        Rejeitar
                    </BotaoPerigoso>
                </HBox>
            ),
        },
    ];

    const discoveredHeaders: DataTableHeader[] = [
        { label: 'IP', column: 'ip', width: '150px' },
        { label: 'MAC', column: 'mac', width: '180px' },
        { label: 'Interface', column: 'interface' },
        {
            label: 'Hostname',
            column: 'netbios_name',
            render: (row: ArpDiscoveredDeviceDTO) => (
                <span style={{ color: row.netbios_name ? undefined : 'var(--tc-color-gray-400)', fontStyle: row.netbios_name ? undefined : 'italic' }}>
                    {row.netbios_name ?? '—'}
                </span>
            ),
        },
        {
            label: 'Tipo',
            width: '120px',
            render: (row: ArpDiscoveredDeviceDTO) => (
                <span style={{ color: row.dynamic ? 'var(--tc-color-gray-600)' : 'var(--tc-color-primary-600)', fontWeight: row.dynamic ? 'normal' : 600 }}>
                    {row.dynamic ? 'Dinâmico' : 'Estático'}
                </span>
            ),
        },
        {
            label: 'Ações',
            width: '120px',
            alignRight: true,
            stickRight: true,
            render: (row: ArpDiscoveredDeviceDTO) => (
                <Botao
                    size="sm"
                    variant="primary"
                    icon={mdiPlus}
                    onClick={() => {
                        setEditObject({ ip: row.ip, mac: row.mac, nome: row.netbios_name || '', appliance_id: applianceId ? +applianceId : undefined });
                        setFieldErrors({});
                    }}
                >
                    Cadastrar
                </Botao>
            ),
        },
    ];

    const editMode = !!editObject?.id;

    return (
        <LayoutUsuario>
            {editObject && (
                <Modal
                    ignoreOverlayClick
                    title={editMode ? 'Editar dispositivo' : 'Novo dispositivo'}
                    show
                    onClose={handleCloseModal}
                    onCancel={handleCloseModal}
                    onConfirm={handleSave}
                >
                    <CampoSeletor
                        label="Appliance"
                        value={editObject.appliance_id != null ? String(editObject.appliance_id) : null}
                        onChange={(v) =>
                            setEditObject((prev) => ({ ...prev, appliance_id: v ? +v : undefined }))
                        }
                        options={applianceOptions}
                        error={fieldErrors?.appliance_id}
                    />
                    <FormLanDevice
                        value={editObject as NetworkLanDeviceDTO}
                        onChange={(v) => setEditObject(v)}
                        fieldErrors={fieldErrors}
                        grupoIds={editGrupoIds}
                        onGrupoIdsChange={setEditGrupoIds}
                        grupoOptions={(gruposQuery.data ?? [])
                            .filter(g => !editObject?.appliance_id || g.appliance_id === editObject.appliance_id)
                            .map(g => ({ id: String(g.id), label: g.nome }))}
                    />
                </Modal>
            )}

            {showGruposModal && (
                <GruposDispositivoManagerModal onClose={() => setShowGruposModal(false)} />
            )}

            {showImportCsvModal && (
                <ImportarCsvModal
                    applianceId={applianceId}
                    onClose={() => setShowImportCsvModal(false)}
                    onImported={() => queryList.refetch()}
                />
            )}

            <InstalarAgenteHostModal
                show={agenteHostDevice !== null}
                device={agenteHostDevice}
                agentId={agenteIdDispositivo(agenteHostDevice)}
                onClose={() => setAgenteHostDevice(null)}
            />

            <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                <PageHeader
                    title={`Dispositivos${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Gerencie os dispositivos cadastrados nas redes LAN"
                    actions={activeTab === 'cadastrados' ? (
                        <>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiFileImport}
                                onClick={() => setShowImportCsvModal(true)}
                                disabled={!applianceId}
                            >
                                Importar CSV
                            </Botao>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiAccountMultiple}
                                onClick={() => setShowGruposModal(true)}
                            >
                                Gerenciar grupos
                            </Botao>
                            <Botao variant="primary" icon={mdiPlus} onClick={handleNew}>
                                Novo
                            </Botao>
                        </>
                    ) : undefined}
                />

                {/* Barra de abas */}
                <HBox gap="0" style={{ borderBottom: '2px solid var(--tc-color-gray-200)', marginBottom: '-8px' }}>
                    <button
                        style={{
                            padding: '8px 20px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'cadastrados' ? '2px solid var(--tc-color-primary-500)' : '2px solid transparent',
                            color: activeTab === 'cadastrados' ? 'var(--tc-color-primary-600)' : 'var(--tc-color-gray-600)',
                            fontWeight: activeTab === 'cadastrados' ? 600 : 400,
                            cursor: 'pointer',
                            marginBottom: '-2px',
                            fontSize: '14px',
                        }}
                        onClick={() => setActiveTab('cadastrados')}
                    >
                        Cadastrados
                    </button>
                    <button
                        style={{
                            padding: '8px 20px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'pendentes' ? '2px solid var(--tc-color-primary-500)' : '2px solid transparent',
                            color: activeTab === 'pendentes' ? 'var(--tc-color-primary-600)' : 'var(--tc-color-gray-600)',
                            fontWeight: activeTab === 'pendentes' ? 600 : 400,
                            cursor: 'pointer',
                            marginBottom: '-2px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        onClick={() => setActiveTab('pendentes')}
                    >
                        <Icon path={mdiLaptop} size="16px" />
                        Aguardando Adoção
                        {(pendentesQuery.data?.length ?? 0) > 0 && (
                            <span style={{
                                background: 'var(--status-warn)',
                                color: 'var(--fg-inverse)',
                                borderRadius: '10px',
                                padding: '1px 7px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}>
                                {pendentesQuery.data!.length}
                            </span>
                        )}
                    </button>
                    <button
                        style={{
                            padding: '8px 20px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'descobertos' ? '2px solid var(--tc-color-primary-500)' : '2px solid transparent',
                            color: activeTab === 'descobertos' ? 'var(--tc-color-primary-600)' : 'var(--tc-color-gray-600)',
                            fontWeight: activeTab === 'descobertos' ? 600 : 400,
                            cursor: 'pointer',
                            marginBottom: '-2px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        onClick={() => setActiveTab('descobertos')}
                    >
                        <Icon path={mdiWifi} size="16px" />
                        Descobertos na Rede
                        {(discoveredQuery.data?.result?.length ?? 0) > 0 && (
                            <span style={{
                                background: 'var(--status-warn)',
                                color: 'var(--fg-inverse)',
                                borderRadius: '10px',
                                padding: '1px 7px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}>
                                {discoveredQuery.data!.result.length}
                            </span>
                        )}
                    </button>
                </HBox>

                {/* Aba: Cadastrados */}
                {activeTab === 'cadastrados' && (
                    <>
                        {!applianceId && (
                            <div style={{ padding: '12px 16px', color: 'var(--tc-color-gray-500)' }}>
                                Selecione uma appliance para ver os dispositivos.
                            </div>
                        )}

                        <HBox gap="12px">
                            <CampoTexto
                                prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
                                placeholder="Pesquisar por nome"
                                value={search}
                                onChange={setSearch}
                            />
                            {(gruposQuery.data?.length ?? 0) > 0 && (
                                <div style={{ width: '240px', flexShrink: 0 }}>
                                    <CampoSeletor
                                        placeholder="Todos os grupos"
                                        value={grupoFiltro}
                                        onChange={setGrupoFiltro}
                                        nullable
                                        options={[
                                            { id: 'nenhum', label: 'Sem grupo' },
                                            ...(gruposQuery.data ?? []).map((g) => ({ id: String(g.id), label: g.nome })),
                                        ]}
                                    />
                                </div>
                            )}
                        </HBox>

                        {queryList.isFetching ? (
                            <div>
                                Aguarde...{' '}
                                <Icon path={mdiLoading} size="14px" color="var(--tc-color-gray-700)" spin />
                            </div>
                        ) : (
                            <div>
                                Exibindo {queryFlatList.length} de {total} resultados.
                            </div>
                        )}

                        <DataTable
                            isLoading={queryList.isFetching}
                            cabecalhos={headers}
                            linhas={queryFlatList}
                            hasMoreData={queryList.hasNextPage}
                            onLoadMore={async () => queryList.fetchNextPage()}
                        />
                    </>
                )}

                {/* Aba: Aguardando Adoção (auto-registrados por host-agent) */}
                {activeTab === 'pendentes' && (
                    <VBox gap="12px" style={{ flex: 1 }}>
                        {!applianceId && (
                            <div style={{ padding: '12px 16px', color: 'var(--tc-color-gray-500)' }}>
                                Selecione uma appliance para ver dispositivos aguardando adoção.
                            </div>
                        )}

                        {applianceId && (
                            <div style={{ padding: '12px 16px', background: 'var(--tc-background-color-info-subtle)', border: '1px solid var(--tc-border-color-info-subtle)', borderRadius: '6px', color: 'var(--tc-text-color-info-emphasis)', fontSize: '13px' }}>
                                Estes dispositivos foram descobertos automaticamente por um host-agent instalado
                                na máquina (reporta CPU, memória e disco). Aceite para adicioná-los à lista de
                                dispositivos cadastrados, ou rejeite para removê-los — se o agente continuar
                                rodando, o dispositivo pode voltar a aparecer aqui no próximo report.
                            </div>
                        )}

                        {applianceId && !pendentesQuery.isFetching && (pendentesQuery.data?.length ?? 0) === 0 && (
                            <div style={{ padding: '12px 16px', color: 'var(--tc-color-gray-500)' }}>
                                Nenhum dispositivo aguardando adoção.
                            </div>
                        )}

                        {(pendentesQuery.data?.length ?? 0) > 0 && (
                            <DataTable
                                isLoading={pendentesQuery.isFetching}
                                cabecalhos={pendentesHeaders}
                                linhas={pendentesQuery.data!}
                                rowStyle={() => ({ background: 'var(--tc-background-color-warning-subtle)', borderLeft: '3px solid var(--status-warn)' })}
                            />
                        )}
                    </VBox>
                )}

                {/* Aba: Descobertos na Rede */}
                {activeTab === 'descobertos' && (
                    <VBox gap="12px" style={{ flex: 1 }}>
                        <HBox style={{ justifyContent: 'flex-end' }}>
                            <Botao
                                variant="outline-secondary"
                                icon={discoveredQuery.isFetching ? mdiLoading : mdiRefresh}
                                onClick={() => discoveredQuery.refetch()}
                                disabled={discoveredQuery.isFetching || !applianceId}
                            >
                                {discoveredQuery.isFetching ? 'Buscando...' : 'Atualizar'}
                            </Botao>
                        </HBox>

                        {discoveredQuery.data?.agentOffline && (
                            <div style={{ padding: '12px 16px', background: 'var(--tc-background-color-warning-subtle)', border: '1px solid var(--tc-border-color-warning-subtle)', borderRadius: '6px', color: 'var(--tc-text-color-warning-emphasis)' }}>
                                Agente não conectado para esta appliance. Verifique se o agente está online.
                            </div>
                        )}

                        {discoveredQuery.data?.error && (
                            <div style={{ padding: '12px 16px', background: 'var(--tc-background-color-danger-subtle)', border: '1px solid var(--tc-border-color-danger-subtle)', borderRadius: '6px', color: 'var(--tc-text-color-danger-emphasis)' }}>
                                Erro ao consultar o agente: {discoveredQuery.data.error}
                            </div>
                        )}

                        {!discoveredQuery.isLoading && !discoveredQuery.data?.agentOffline && !discoveredQuery.data?.error && (discoveredQuery.data?.result?.length ?? 0) === 0 && !discoveredQuery.isFetching && (
                            <div style={{ padding: '12px 16px', color: 'var(--tc-color-gray-500)' }}>
                                {applianceId ? 'Nenhum dispositivo novo encontrado na rede.' : 'Selecione uma appliance para descobrir dispositivos.'}
                            </div>
                        )}

                        {(discoveredQuery.data?.result?.length ?? 0) > 0 && (
                            <DataTable
                                isLoading={discoveredQuery.isFetching}
                                cabecalhos={discoveredHeaders}
                                linhas={discoveredQuery.data!.result}
                                rowStyle={() => ({ background: 'var(--tc-background-color-warning-subtle)', borderLeft: '3px solid var(--status-warn)' })}
                            />
                        )}
                    </VBox>
                )}
            </VBox>

            <TelemetryOverviewModal
                show={telemetryDevice !== null}
                title={telemetryDevice ? `Telemetria: ${telemetryDevice.nome}` : 'Telemetria'}
                onClose={() => setTelemetryDevice(null)}
                overviewUrl={(period) => `/api/telemetry/resources/device/${telemetryDevice!.id}/overview?window=${period}`}
                errorMessage="Erro ao carregar visão de telemetria do dispositivo."
            >
                {telemetryDevice && (telemetryDevice.os_platform || telemetryDevice.os_version) && (
                    <span style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                        {formatOsLabel(telemetryDevice.os_platform, telemetryDevice.os_version, telemetryDevice.os_arch)}
                    </span>
                )}
            </TelemetryOverviewModal>

            <Modal
                show={osPatchDevice !== null}
                title={osPatchDevice ? `Patches de segurança: ${osPatchDevice.nome}` : 'Patches de segurança'}
                onClose={() => setOsPatchDevice(null)}
                onCancel={() => setOsPatchDevice(null)}
                hideControls
                size="lg"
            >
                <VBox style={{ gap: '16px' }}>
                    {osPatchDevice && (osPatchDevice.os_platform || osPatchDevice.os_version) && (
                        <span style={{ fontSize: '13px', color: 'var(--color-muted)' }}>
                            {formatOsLabel(osPatchDevice.os_platform, osPatchDevice.os_version, osPatchDevice.os_arch)}
                        </span>
                    )}

                    {queryOsPatchDetail.isLoading && (
                        <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                            <Icon path={mdiLoading} spin size={1.5} />
                        </HBox>
                    )}

                    {queryOsPatchDetail.isError && (
                        <p>Erro ao carregar patches do dispositivo.</p>
                    )}

                    {!queryOsPatchDetail.isLoading && !queryOsPatchDetail.isError && queryOsPatchDetail.data && (
                        queryOsPatchDetail.data.missing.length === 0 ? (
                            <span style={{ color: 'var(--color-muted)' }}>Nenhum patch pendente — dispositivo em dia.</span>
                        ) : (
                            <VBox style={{ gap: '8px' }}>
                                {queryOsPatchDetail.data.missing.map((item, idx) => {
                                    const meta = SEVERITY_META[item.severity];
                                    return (
                                        <HBox
                                            key={`${item.package_name}-${idx}`}
                                            style={{
                                                gap: '12px',
                                                alignItems: 'center',
                                                border: '1px solid var(--border-1)',
                                                borderRadius: '10px',
                                                padding: '10px 14px',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <VBox style={{ gap: '2px' }}>
                                                <span style={{ fontWeight: 600, fontSize: '13px' }}>{item.package_name}</span>
                                                <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>
                                                    {item.installed_version ? `instalado: ${item.installed_version} · ` : ''}
                                                    {item.available_version ? `disponível: ${item.available_version} · ` : ''}
                                                    {item.advisory_id ? `${item.advisory_id} · ` : ''}
                                                    {item.source}
                                                </span>
                                            </VBox>
                                            <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                                {item.reboot_hint && (
                                                    <Icon
                                                        path={mdiRestart}
                                                        size="16px"
                                                        color="var(--tc-color-warning-500, #b45309)"
                                                        title={item.reboot_hint === 'required'
                                                            ? 'Vai exigir reinício após aplicado'
                                                            : 'Pode exigir reinício após aplicado'}
                                                    />
                                                )}
                                                <Badge variant={meta.variant}>{meta.label}</Badge>
                                            </HBox>
                                        </HBox>
                                    );
                                })}
                            </VBox>
                        )
                    )}
                </VBox>
            </Modal>
        </LayoutUsuario>
    );
}
